import { useEffect, useState } from 'react';
import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import Rolling from '@assets/gif/rolling.gif';
import ImagePlaceholderPNG from '@assets/png/manufacturer-placeholder.png';
import ProductPlaceholderPNG from '@assets/png/product.png';

import { IProduct } from '@models/Product.model';
import { IManufacturer } from '@models/Manufacturer.model';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { indexManufacturersPagination } from '@services/queries/manufacturers';
import { getProducts } from '@services/queries/products';
import { useInView } from 'react-intersection-observer';
import { Shimmer } from '@components/index';

import { addTaskProducts } from '@services/queries/tasks';
import {
  Body,
  ButtonMain,
  ButtonSimple,
  CloseButton,
  Container,
  Description,
  Header,
  HeaderBar,
  Overlay,
  Buttons,
  Inputs,
  InputContainer,
  Input,
  IconLeft,
  GlassIcon,
  CleanSearch,
  CleanButton,
  Skipped,
  ResultsText,
  ScrollView,
  LoadMore,
  LoadingText,
  LoadingImg,
  ManufacturerContainer,
  ProductContainer,
  LoadMoreContainer,
  Radio,
  ItemName,
  ItemImage,
  ProductValue,
  FullDetailsButton,
  InternalContainer,
  InternalText,
  LoadingSearch,
  ProductTitles,
  TitleTable,
  TitleTableSpace,
  ItemEmpty,
} from './AddProducts.styles';
import { ProductDetail } from './components';

interface OwnProps {
  open: boolean;
  onClose: () => void;
  taskId: string;
  productsIdsAdded: string[];
}

export const AddProducts = ({
  open,
  onClose,
  taskId,
  productsIdsAdded,
}: OwnProps) => {
  const portalDiv = document.getElementById('portal');

  const queryClient = useQueryClient();

  const manufacturersInView = useInView();
  const productsInView = useInView();

  const [productDetailOpen, setProductDetailOpen] = useState<boolean>(false);

  const [productsList, setProductsList] = useState<string[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);

  const [manufacturer, setManufacturer] = useState<IManufacturer | null>(null);
  const [selectedManufacturer, setSelectedManufacturer] =
    useState<IManufacturer | null>(null);
  const [skipManufacturer, setSkipManufacturer] = useState<boolean>(false);
  const [searchManufacturer, setSearchManufacturer] = useState<string>('');
  const [searchManufacturerEnter, setSearchManufacturerEnter] =
    useState<string>('');
  const [searchProduct, setSearchProduct] = useState<string>('');
  const [searchProductEnter, setSearchProductEnter] = useState<string>('');

  const manufacturersQuery = useInfiniteQuery(
    ['AddProducts', 'manufacturers', searchManufacturerEnter],
    async ({ pageParam = 1 }) => {
      return indexManufacturersPagination({
        pagination: {
          limit: 20,
          page: pageParam,
          search: searchManufacturerEnter,
        },
      });
    },
    {
      getPreviousPageParam: undefined,
      getNextPageParam: lastPage =>
        lastPage.hasMore ? lastPage.currentPage + 1 : undefined,
      refetchOnWindowFocus: false,
      enabled: !!searchManufacturerEnter,
    },
  );

  const productsQuery = useInfiniteQuery(
    ['AddProducts', 'products', manufacturer?.name, searchProductEnter],
    async ({ pageParam = 1 }) => {
      return getProducts({
        pagination: {
          limit: 20,
          page: pageParam,
          search: searchProductEnter,
        },
        filter: manufacturer
          ? {
              manufacturerId: manufacturer.id,
              sort: 'product.name',
              direction: 'ASC',
            }
          : { sort: 'product.name', direction: 'ASC' },
      });
    },
    {
      getPreviousPageParam: undefined,
      getNextPageParam: lastPage =>
        lastPage.hasMore ? lastPage.currentPage + 1 : undefined,
      refetchOnWindowFocus: false,
    },
  );

  const hasMoreManufacturers = manufacturersQuery.data
    ? manufacturersQuery.data.pages[manufacturersQuery.data.pages.length - 1]
        .hasMore
    : false;

  const hasMoreProducts = productsQuery.data
    ? productsQuery.data.pages[productsQuery.data.pages.length - 1].hasMore
    : false;

  const manufacturers = manufacturersQuery.data
    ? manufacturersQuery.data.pages
        .map(pageData => pageData.results)
        .reduce(function reduce(prev, next) {
          return prev.concat(next);
        })
    : [];

  const products = productsQuery.data
    ? productsQuery.data.pages
        .map(pageData => pageData.results)
        .reduce(function reduce(prev, next) {
          return prev.concat(next);
        })
    : [];

  const mutationAddProductsToTask = useMutation(addTaskProducts, {
    onSuccess: () => {
      onClose();

      queryClient.invalidateQueries(['tasks']);
    },
  });

  const confirmLoading = mutationAddProductsToTask.isLoading;

  useEffect(() => {
    if (manufacturersInView.inView && hasMoreManufacturers)
      manufacturersQuery.fetchNextPage();
  }, [manufacturersInView.inView, hasMoreManufacturers]);

  useEffect(() => {
    if (productsInView.inView && hasMoreProducts) productsQuery.fetchNextPage();
  }, [productsInView.inView, hasMoreProducts]);

  const resultsText = () => {
    if (!manufacturer && !skipManufacturer) {
      if (searchManufacturerEnter) {
        if (manufacturersQuery.isLoading) {
          return 'Loading results';
        }

        if (manufacturers.length === 0) {
          return 'No results found';
        }

        return `${manufacturersQuery.data?.pages[0].total} Results found`;
      }

      return 'Search for a Manufacturer to see results';
    }

    if (productsQuery.isLoading) {
      return 'Loading results';
    }

    if (products.length === 0) {
      return 'No results found';
    }

    return `${productsQuery.data?.pages[0].total} Results found`;
  };

  const toggleProduct = (id: string) => {
    if (productsList.includes(id)) {
      setProductsList(productsList.filter(item => item !== id));
    } else {
      setProductsList([...productsList, id]);
    }
  };

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <HeaderBar>
              <Header>Add Relationships</Header>
              <CloseButton onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </CloseButton>
            </HeaderBar>
            <Body>
              <Description>
                Find products and add as a relationship to your task
              </Description>

              <Inputs>
                <InputContainer>
                  {!!manufacturer && (
                    <IconLeft
                      src={manufacturer.imageUrl || ImagePlaceholderPNG}
                      brokenSrc={ImagePlaceholderPNG}
                    />
                  )}
                  {!manufacturer && (
                    <GlassIcon>
                      <BlueIcon icon="search" color="#8F95B2" size={12} />
                    </GlassIcon>
                  )}
                  <Input
                    placeholder="Search Manufacturer"
                    disabled={skipManufacturer || confirmLoading}
                    isSearch={!manufacturer}
                    value={searchManufacturer}
                    onChange={e => setSearchManufacturer(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        setSearchManufacturerEnter(searchManufacturer);
                      }

                      setManufacturer(null);
                      setSelectedManufacturer(null);
                      setSearchProduct('');
                      setSearchProductEnter('');
                    }}
                  />
                  {skipManufacturer && <Skipped>Skipped</Skipped>}
                  {searchManufacturer &&
                    !confirmLoading &&
                    !manufacturersQuery.isLoading && (
                      <CleanSearch>
                        <CleanButton
                          onClick={() => {
                            setSearchManufacturer('');
                            setSearchManufacturerEnter('');
                            setSearchProduct('');
                            setSearchProductEnter('');
                            setManufacturer(null);
                            setSelectedManufacturer(null);
                          }}
                        >
                          <BlueIcon icon="cross" color="#8F95B2" size={12} />
                        </CleanButton>
                      </CleanSearch>
                    )}
                  {!manufacturer &&
                    !skipManufacturer &&
                    manufacturersQuery.isLoading && (
                      <LoadingSearch src={Rolling} alt="loading" />
                    )}
                </InputContainer>
                <InputContainer>
                  <GlassIcon>
                    <BlueIcon icon="search" color="#8F95B2" size={12} />
                  </GlassIcon>
                  <Input
                    placeholder="Type a Product Name or a Serial #"
                    disabled={
                      (!manufacturer && !skipManufacturer) || confirmLoading
                    }
                    isSearch
                    value={searchProduct}
                    onChange={e => setSearchProduct(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        setSearchProductEnter(searchProduct);
                      }
                    }}
                  />
                  {searchProduct &&
                    !confirmLoading &&
                    !productsQuery.isLoading && (
                      <CleanSearch>
                        <CleanButton
                          onClick={() => {
                            setSearchProduct('');
                            setSearchProductEnter('');
                            setProductsList([]);
                          }}
                        >
                          <BlueIcon icon="cross" color="#8F95B2" size={12} />
                        </CleanButton>
                      </CleanSearch>
                    )}
                  {(!!manufacturer || skipManufacturer) &&
                    productsQuery.isLoading && (
                      <LoadingSearch src={Rolling} alt="loading" />
                    )}
                </InputContainer>
              </Inputs>
              <ResultsText>{resultsText()}</ResultsText>
              {!manufacturer && !skipManufacturer && searchManufacturerEnter && (
                <ProductTitles>
                  <TitleTable>
                    <TitleTableSpace /> Manufacturer Name
                  </TitleTable>
                </ProductTitles>
              )}
              {(!!manufacturer || skipManufacturer) && searchProductEnter && (
                <ProductTitles>
                  <TitleTable>
                    <TitleTableSpace /> Product Name
                  </TitleTable>
                  <TitleTable>Model #</TitleTable>
                  <TitleTable>Manufacturer</TitleTable>
                  <TitleTable />
                </ProductTitles>
              )}
              <ScrollView>
                {(manufacturersQuery.isLoading ||
                  (productsQuery.isLoading &&
                    (manufacturer || skipManufacturer))) && (
                  <>
                    <Shimmer
                      style={{ height: 44, width: '100%', borderRadius: 4 }}
                      primary="#f1f1f1"
                      secondary="#ececec"
                    />
                    <Shimmer
                      style={{
                        height: 44,
                        width: '100%',
                        borderRadius: 4,
                        marginTop: 4,
                      }}
                      primary="#f1f1f1"
                      secondary="#ececec"
                    />
                    <Shimmer
                      style={{
                        height: 44,
                        width: '100%',
                        borderRadius: 4,
                        marginTop: 4,
                      }}
                      primary="#f1f1f1"
                      secondary="#ececec"
                    />
                    <Shimmer
                      style={{
                        height: 44,
                        width: '100%',
                        borderRadius: 4,
                        marginTop: 4,
                      }}
                      primary="#f1f1f1"
                      secondary="#ececec"
                    />
                  </>
                )}
                {!manufacturer && !skipManufacturer && searchManufacturerEnter && (
                  <>
                    {manufacturers.map(item => (
                      <ManufacturerContainer
                        key={item.id}
                        onClick={() => {
                          setSelectedManufacturer(item);
                        }}
                      >
                        <Radio active={selectedManufacturer?.id === item.id} />
                        <ItemImage
                          src={item.imageUrl || ImagePlaceholderPNG}
                          brokenSrc={ImagePlaceholderPNG}
                        />
                        {item.name ? (
                          <ItemName>{item.name}</ItemName>
                        ) : (
                          <ItemEmpty>Empty</ItemEmpty>
                        )}
                        {item.status === 'internal' && (
                          <InternalContainer>
                            <BlueIcon icon="issue" color="#8F95B2" size={12} />
                            <InternalText>Internal</InternalText>
                          </InternalContainer>
                        )}
                      </ManufacturerContainer>
                    ))}
                    <LoadMoreContainer ref={manufacturersInView.ref}>
                      {hasMoreManufacturers ? (
                        <LoadMore>
                          <LoadingText>Loading more</LoadingText>
                          <LoadingImg src={Rolling} alt="loading" />
                        </LoadMore>
                      ) : (
                        <div />
                      )}
                    </LoadMoreContainer>
                  </>
                )}
                {(!!manufacturer || skipManufacturer) &&
                  !productsQuery.isLoading && (
                    <>
                      {products.map(item => (
                        <ProductContainer
                          key={item.id}
                          onClick={() => {
                            if (
                              !confirmLoading &&
                              !productsIdsAdded.includes(item.id)
                            ) {
                              toggleProduct(item.id);
                            }
                          }}
                        >
                          <ProductValue>
                            <Radio active={productsList.includes(item.id)} />
                            <ItemImage
                              src={item.imageUrl || ProductPlaceholderPNG}
                              brokenSrc={ProductPlaceholderPNG}
                            />
                            {productsIdsAdded.includes(item.id) && (
                              <ItemEmpty>(Added)</ItemEmpty>
                            )}
                            {item.name ? (
                              <ItemName>{item.name}</ItemName>
                            ) : (
                              <ItemEmpty>Empty</ItemEmpty>
                            )}
                          </ProductValue>
                          <ProductValue>
                            {item.model ? (
                              <ItemName>{item.model}</ItemName>
                            ) : (
                              <ItemEmpty>Empty</ItemEmpty>
                            )}
                          </ProductValue>
                          <ProductValue>
                            <ItemName>{item.manufacturer?.name}</ItemName>
                          </ProductValue>
                          <ProductValue>
                            {item.status === 'internal' ? (
                              <InternalContainer>
                                <BlueIcon
                                  icon="issue"
                                  color="#8F95B2"
                                  size={12}
                                />
                                <InternalText>Internal</InternalText>
                              </InternalContainer>
                            ) : (
                              <FullDetailsButton
                                onClick={e => {
                                  e.stopPropagation();
                                  setProductDetailOpen(true);
                                  setSelectedProduct(item);
                                }}
                              >
                                Full Details
                              </FullDetailsButton>
                            )}
                          </ProductValue>
                        </ProductContainer>
                      ))}
                      <LoadMoreContainer ref={productsInView.ref}>
                        {hasMoreProducts ? (
                          <LoadMore>
                            <LoadingText>Loading more</LoadingText>
                            <LoadingImg src={Rolling} alt="loading" />
                          </LoadMore>
                        ) : (
                          <div />
                        )}
                      </LoadMoreContainer>
                    </>
                  )}
              </ScrollView>
            </Body>
            <Buttons>
              {!manufacturer && !skipManufacturer && (
                <ButtonSimple
                  onClick={() => {
                    setSkipManufacturer(true);
                    setManufacturer(null);
                    setSearchManufacturer('');
                    setSearchManufacturerEnter('');
                  }}
                >
                  Skip Manufacturer
                </ButtonSimple>
              )}
              {(manufacturer || skipManufacturer) && (
                <ButtonSimple
                  onClick={() => {
                    if (!confirmLoading) {
                      setManufacturer(null);
                      setProductsList([]);
                      setSkipManufacturer(false);
                      setSearchProduct('');
                      setSearchProductEnter('');
                    }
                  }}
                >
                  Back
                </ButtonSimple>
              )}
              {!manufacturer && !skipManufacturer && (
                <ButtonMain
                  disabled={!selectedManufacturer}
                  onClick={() => {
                    setManufacturer(selectedManufacturer);
                    if (selectedManufacturer) {
                      setSearchManufacturer(selectedManufacturer.name);
                    }
                  }}
                >
                  Continue
                </ButtonMain>
              )}
              {(manufacturer || skipManufacturer) && (
                <ButtonMain
                  disabled={productsList.length === 0 || confirmLoading}
                  onClick={() => {
                    if (productsList.length > 0 && !confirmLoading) {
                      mutationAddProductsToTask.mutate({
                        taskId,
                        productsIds: productsList,
                      });
                    }
                  }}
                >
                  Add Relationships
                </ButtonMain>
              )}
              {productDetailOpen && selectedProduct && (
                <ProductDetail
                  open={productDetailOpen}
                  onClose={() => setProductDetailOpen(false)}
                  product={selectedProduct}
                  onSelectProduct={() => {
                    toggleProduct(selectedProduct.id);
                    setProductDetailOpen(false);
                  }}
                />
              )}
            </Buttons>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
